import React from 'react'
import { useCallback } from 'sb-hooks'
import { useIntl } from 'sb-react-intl'
import { Field, useFieldState } from 'formular'

import { SelectableOptionView, SelectableOptionViewProps } from 'components/_new/controls'
import { Box } from 'components/_new/layout'
import Text from 'components/_new/typography/Text/Text'
import { TextareaField } from 'components/_new/forms'

import messages from './messages'
import s from './SelectableReasonField.scss'


type SelectableReasonFieldProps = {
  checkedField: Field<boolean | string> // field for checked state
  commentsField?: Field<string>  // field for comments state
  title?: ReactIntlMessage | string // reason content
  type?: SelectableOptionViewProps['type']
  value?: string // value for radio
  onClick?: (checked: boolean) => void
  dataTestId?: string
}

const SelectableReasonField: React.FunctionComponent<SelectableReasonFieldProps> = (props) => {
  const {
    children, title, type = 'checkbox', value, checkedField, commentsField, onClick, dataTestId = 'reason',
  } = props

  const intl = useIntl()

  const { value: fieldValue } = useFieldState(checkedField)

  // for radio and checkbox
  const checked = typeof value === 'string' ? fieldValue === value : Boolean(fieldValue)

  const handleClick = useCallback(() => {
    if (typeof value === 'string') {
      checkedField.set(value)
    }
    else {
      checkedField.set(!checked)
    }

    if (typeof onClick === 'function') {
      onClick(!checked)
    }
  }, [ checkedField, value, checked, onClick ])

  const handleFieldClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  return (
    <SelectableOptionView
      checked={Boolean(checked)}
      type={type}
      onClick={handleClick}
      dataTestId={dataTestId}
    >
      {
        Boolean(title) && (
          <Text size="16-24" message={title} dataTestId={`${dataTestId}Title`} />
        )
      }
      {children}
      {
        Boolean(checked && commentsField) && (
          <Box mt={16} noWrapper>
            <div onClick={handleFieldClick}>
              <TextareaField
                className={s.comments}
                field={commentsField}
                placeholder={intl.formatMessage(messages.commentsPlaceholder)}
                focusOnInit
                dataTestId={`${dataTestId}Comments`}
              />
            </div>
          </Box>
        )
      }
    </SelectableOptionView>
  )
}


export default SelectableReasonField
